/**
 * Capture une image PNG (dataURL) de l'aperçu d'un configurateur.
 *
 * Utilise html2canvas pour rasteriser fidèlement le DOM rendu (incluant les
 * polices custom, halos LED, masques, dégradés CSS). Le dataURL est stocké
 * dans `configuration.previewImage` au moment de l'ajout au panier, puis
 * embarqué dans la fiche de production PDF côté serveur.
 *
 * IMPORTANT : la qualité est volontairement limitée (scale 1, JPEG 70%) car
 * - Le dataURL part dans localStorage (limite ~5 MB par site)
 * - Il transite ensuite vers le backend en JSON pour la génération PDF
 * - L'aperçu PDF n'a besoin que d'~250-400 px de large
 */
import html2canvas from "html2canvas";

const PNG_MAX_KB = 1400; // marge confortable sous la limite localStorage

/**
 * Capture l'élément `node` et renvoie un dataURL JPEG (plus léger que PNG).
 * En cas d'échec (CORS, font loading, etc.) renvoie null silencieusement —
 * la fiche PDF aura juste un cadre vide à la place de la preview.
 */
export async function snapshotConfiguratorPreview(node, opts = {}) {
  if (!node) return null;
  const {
    backgroundColor = "#0a0a0a",
    scale = 1.5,
    maxWidth = 1100,
    quality: initialQuality = 0.82,
  } = opts;
  try {
    // Attendre un tick pour s'assurer que les fonts custom sont chargées
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    const canvas = await html2canvas(node, {
      backgroundColor,
      scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      // imageTimeout 0 = pas de timeout sur les images CORS
      imageTimeout: 0,
      // On IGNORE tous les éléments marqués `data-snapshot-hide` afin de
      // capturer UNIQUEMENT le produit configuré (pas la façade / scène de fond).
      ignoreElements: (el) => {
        try {
          return el.getAttribute && el.getAttribute("data-snapshot-hide") === "true";
        } catch {
          return false;
        }
      },
    });

    // Redimensionne pour rester sous la limite de poids
    let finalCanvas = canvas;
    if (canvas.width > maxWidth) {
      const ratio = maxWidth / canvas.width;
      const dst = document.createElement("canvas");
      dst.width = maxWidth;
      dst.height = Math.round(canvas.height * ratio);
      const ctx = dst.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(canvas, 0, 0, dst.width, dst.height);
      finalCanvas = dst;
    }

    // JPEG plus compact que PNG pour photos / dégradés
    let quality = initialQuality;
    let dataUrl = finalCanvas.toDataURL("image/jpeg", quality);
    // Si encore trop lourd, réduit la qualité progressivement
    while (dataUrl.length > PNG_MAX_KB * 1024 && quality > 0.4) {
      quality -= 0.15;
      dataUrl = finalCanvas.toDataURL("image/jpeg", quality);
    }
    return dataUrl;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[snapshot] preview capture failed:", e);
    return null;
  }
}
